import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, Users, Package2, CheckSquare, FileText } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export type SearchCategory = 'all' | 'clients' | 'orders' | 'tasks' | 'invoices';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  category: SearchCategory;
  url: string;
}

const CentralizedSearchBar: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<SearchCategory>('all');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<Pick<React.CSSProperties, 'top' | 'left' | 'width'>>();

  const categories = [
    { value: 'all' as SearchCategory, label: 'All', icon: Search },
    { value: 'clients' as SearchCategory, label: 'Clients', icon: Users },
    { value: 'orders' as SearchCategory, label: 'Orders', icon: Package2 },
    { value: 'tasks' as SearchCategory, label: 'Tasks', icon: CheckSquare },
    { value: 'invoices' as SearchCategory, label: 'Invoices', icon: FileText },
  ];

  // Fonction pour rechercher dans la base de données
  const searchInDatabase = async (term: string, category: SearchCategory) => {
    if (!user || !supabase) return [];

    const results: SearchResult[] = [];
    const searchTerm = term.toLowerCase();

    try {
      // Rechercher les clients
      if (category === 'all' || category === 'clients') {
        const { data: clients, error: clientsError } = await supabase
          .from('clients')
          .select('id, name, email, company, platform')
          .eq('user_id', user.id)
          .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%`);

        if (!clientsError && clients) {
          clients.forEach(client => {
            results.push({
              id: client.id,
              title: client.name || 'Unnamed Client',
              subtitle: `${client.company || 'No Company'} • ${client.platform || 'No Platform'}`,
              category: 'clients',
              url: '/clients'
            });
          });
        }
      }

      // Rechercher les commandes
      if (category === 'all' || category === 'orders') {
        const { data: orders, error: ordersError } = await supabase
          .from('orders')
          .select('id, title, client_name, platform, status')
          .eq('user_id', user.id)
          .or(`title.ilike.%${searchTerm}%,client_name.ilike.%${searchTerm}%`);

        if (!ordersError && orders) {
          orders.forEach(order => {
            results.push({
              id: order.id,
              title: order.title || 'Untitled Order',
              subtitle: `${order.client_name || 'No Client'} • ${order.platform || 'No Platform'}`,
              category: 'orders',
              url: '/orders'
            });
          });
        }
      }

      // Rechercher les tâches
      if (category === 'all' || category === 'tasks') {
        const { data: tasks, error: tasksError } = await supabase
          .from('tasks')
          .select('id, title, description, status, priority')
          .eq('user_id', user.id)
          .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);

        if (!tasksError && tasks) {
          tasks.forEach(task => {
            results.push({
              id: task.id,
              title: task.title || 'Untitled Task',
              subtitle: `${task.status || 'No Status'} • ${task.priority || 'No Priority'}`,
              category: 'tasks',
              url: '/tasks'
            });
          });
        }
      }

      // Rechercher les factures
      if (category === 'all' || category === 'invoices') {
        try {
          // D'abord, chercher les clients qui correspondent au terme de recherche
          const { data: matchingClients } = await supabase
            .from('clients')
            .select('id, name')
            .eq('user_id', user.id)
            .ilike('name', `%${searchTerm}%`);

          const clientIds = matchingClients?.map(c => c.id) || [];
          
          // Chercher les factures par numéro OU par client_id
          let invoices: any[] = [];
          
          // Chercher par numéro de facture
          const { data: invoicesByNumber, error: errorByNumber } = await supabase
            .from('invoices')
            .select('id, number, total, status, client_id')
            .eq('user_id', user.id)
            .ilike('number', `%${searchTerm}%`);

          if (!errorByNumber && invoicesByNumber) {
            invoices = [...invoicesByNumber];
          }

          // Chercher par client_id si on a des clients correspondants
          if (clientIds.length > 0) {
            const { data: invoicesByClient, error: errorByClient } = await supabase
              .from('invoices')
              .select('id, number, total, status, client_id')
              .eq('user_id', user.id)
              .in('client_id', clientIds);

            if (!errorByClient && invoicesByClient) {
              // Fusionner les résultats et supprimer les doublons
              const existingIds = new Set(invoices.map(inv => inv.id));
              invoicesByClient.forEach(inv => {
                if (!existingIds.has(inv.id)) {
                  invoices.push(inv);
                }
              });
            }
          }

          // Traiter les résultats si on a des factures
          if (invoices.length > 0) {
            // Créer une map des clients pour référence rapide
            const clientNamesMap: Record<string, string> = {};
            if (matchingClients) {
              matchingClients.forEach(client => {
                clientNamesMap[client.id] = client.name || 'No Client';
              });
            }
            
            // Si on n'a pas tous les clients, récupérer ceux qui manquent
            const missingClientIds = invoices
              .map(inv => inv.client_id)
              .filter(id => id && !clientNamesMap[id]);
            
            if (missingClientIds.length > 0) {
              const { data: additionalClients } = await supabase
                .from('clients')
                .select('id, name')
                .in('id', missingClientIds);
              
              if (additionalClients) {
                additionalClients.forEach(client => {
                  clientNamesMap[client.id] = client.name || 'No Client';
                });
              }
            }
            
            invoices.forEach(invoice => {
              const clientName = invoice.client_id ? (clientNamesMap[invoice.client_id] || 'No Client') : 'No Client';
              results.push({
                id: invoice.id,
                title: invoice.number || 'No Invoice Number',
                subtitle: `${clientName} • ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(invoice.total || 0)}`,
                category: 'invoices',
                url: '/invoices'
              });
            });
          }
        } catch (error) {
          console.error('Error searching invoices:', error);
        }
      }

    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      // En cas d'erreur, retourner un résultat vide plutôt que de planter
    }

    return results;
  };

  // Filtrer les résultats
  useEffect(() => {
    if (!searchTerm.trim() || !user) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    
    // Délai pour éviter trop de requêtes
    const timer = setTimeout(async () => {
      const searchResults = await searchInDatabase(searchTerm, selectedCategory);
      setResults(searchResults);
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory, user]);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (result: SearchResult) => {
    // Navigation vers la page avec l'élément spécifique si possible
    if (result.category === 'clients') {
      navigate(`/clients?search=${encodeURIComponent(result.title)}`);
    } else if (result.category === 'orders') {
      navigate(`/orders?search=${encodeURIComponent(result.title)}`);
    } else if (result.category === 'tasks') {
      navigate(`/tasks?search=${encodeURIComponent(result.title)}`);
    } else if (result.category === 'invoices') {
      navigate(`/invoices?search=${encodeURIComponent(result.title)}`);
    } else {
      navigate(result.url);
    }
    
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setResults([]);
    }
    // Ctrl+K ou Cmd+K pour ouvrir la recherche
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };
  
  // Global keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        inputRef.current?.focus();
      }
    };
    
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
    inputRef.current?.focus();
  };

  const updateMenuPosition = useCallback(() => {
    if (!isOpen || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const minWidth = Math.min(600, Math.max(rect.width, 400));
    const viewportWidth = window.innerWidth;
    const horizontalPadding = 16;

    let left = rect.left;
    if (left + minWidth > viewportWidth - horizontalPadding) {
      left = viewportWidth - horizontalPadding - minWidth;
    }
    left = Math.max(left, horizontalPadding);

    setMenuStyle({
      top: rect.bottom + 8,
      left,
      width: minWidth,
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setMenuStyle(undefined);
      return;
    }

    updateMenuPosition();

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const dropdownNode =
    isOpen && menuStyle
      ? createPortal(
          <div
            ref={dropdownRef}
            className="theme-card-bg theme-card-border rounded-lg shadow-lg z-[60] overflow-hidden border"
            style={{
              position: 'fixed',
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
            }}
          >
            {/* Liste des catégories/filtres */}
            <div className="p-3 border-b theme-card-border">
              <div className="text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-3">
                {'Filter by'}
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.value}
                      onClick={() => {
                        setSelectedCategory(category.value);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors border ${
                        selectedCategory === category.value
                          ? 'theme-btn-secondary theme-text-primary theme-card-border font-medium'
                          : 'theme-text-secondary hover:theme-hover-button theme-card-border'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{category.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Résultats de recherche */}
            {searchTerm && (
              <div className="max-h-64 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 text-center theme-text-secondary">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <div className="text-sm theme-text-secondary">Loading...</div>
                  </div>
                ) : results.length > 0 ? (
                  <div className="p-2">
                    <div className="text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-2 px-2">
                      Results
                    </div>
                    {results.map((result) => {
                      const CategoryIcon = categories.find(c => c.value === result.category)?.icon || Search;
                      return (
                        <button
                          key={result.id}
                          onClick={() => handleSearch(result)}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:theme-hover-button
                                     text-left transition-colors group"
                        >
                          <CategoryIcon className="h-4 w-4 theme-text-secondary" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium theme-text-primary group-hover:theme-text-primary truncate">
                              {result.title}
                            </div>
                            {result.subtitle && (
                              <div className="text-xs theme-text-secondary truncate">
                                {result.subtitle}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 text-center theme-text-secondary">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50 theme-text-secondary" />
                    <div className="text-sm theme-text-secondary">No results found</div>
                    <div className="text-xs theme-text-tertiary mt-1">Try a different search term</div>
                  </div>
                )}
              </div>
            )}
            
            {/* Message quand pas de recherche */}
            {!searchTerm && (
              <div className="p-4 text-center theme-text-secondary">
                <div className="text-sm theme-text-secondary">Start typing to search...</div>
                <div className="text-xs theme-text-tertiary mt-1">Use ⌘K to focus search</div>
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Barre de recherche */}
      <div className="relative">
        <div className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10">
          <Search className="h-3.5 w-3.5 text-[rgb(var(--muted))]" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={'Search...'}
          className="w-full max-w-[400px] pl-8 pr-20 py-1.5 rounded-full border-0
                     theme-input theme-text-primary placeholder-gray-400
                     focus:outline-none focus:ring-0
                     transition-all duration-200 text-sm"
        />

        {/* Raccourci clavier style Shopify - DANS la barre de recherche */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="p-1 rounded-lg hover:theme-hover-button text-[rgb(var(--muted))] hover:theme-text-primary transition-all duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {dropdownNode}
    </div>
  );
};

export default CentralizedSearchBar;
